<template>
  <div v-if="show" class="popup-overlay">
    <div class="popup-dialog" :class="typeClass">
      <div class="popup-header">
        <h3 class="dialog-title">{{ title }}</h3>
      </div>
      <div class="popup-content">
        <p>{{ message }}</p>
      </div>
      <div class="popup-actions">
        <button 
          v-if="showCancel" 
          class="popup-btn popup-secondary" 
          @click="closeDialog"
          :disabled="isLoading">
          {{ cancelButtonText }}
        </button>
        <button 
          class="popup-btn popup-primary" 
          @click="handlePrimaryAction"
          :disabled="isLoading">
          <span v-if="!isLoading">{{ primaryButtonText }}</span>
          <span v-else class="spinner"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

// Props definition (using compiler macro)
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'info', // 'success', 'error', 'warning', 'info'
    validator: value => ['success', 'error', 'warning', 'info'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    required: true
  },
  primaryButtonText: {
    type: String,
    default: 'OK'
  },
  cancelButtonText: {
    type: String,
    default: 'Cancel'
  },
  showCancel: {
    type: Boolean,
    default: false
  },
  isLoading: {
    type: Boolean,
    default: false
  }
});

// Emits definition (using compiler macro)
const emit = defineEmits(['close', 'confirm']);

// Computed properties
const typeClass = computed(() => `popup-${props.type}`);

// Methods
const closeDialog = () => {
  emit('close');
};

const handlePrimaryAction = () => {
  emit('confirm');
};
</script>

<style scoped>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.18);
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.popup-dialog {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 32px rgba(0,0,0,0.13);
  padding: 32px 28px 24px 28px;
  min-width: 320px;
  max-width: 94vw;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: relative;
  animation: dialogSlideIn 0.3s ease-out;
}

.popup-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 8px;
}

.popup-title {
  font-family: 'LemonCupcakeBold', 'Pacifico', 'Segoe Script', 'Brush Script MT', cursive;
  font-weight: 300;
  font-size: 2.5rem;
  color: #312d30;
  margin: 0;
  text-align: center;
  line-height: 1.3;
}

.popup-content {
  text-align: center;
  margin: 10px 0 20px 0;
}

.popup-content p {
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  font-size: 1rem;
  color: #312d30;
  margin: 0;
  line-height: 1.5;
  font-weight: 500;
}

.popup-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
}

.popup-btn {
  padding: 7px 18px;
  border-radius: 4px;
  border: none;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-primary {
  background: #7da47e;
  color: #fff;
}

.popup-primary:hover:not(:disabled) {
  background: #388E3C;
}

.popup-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.popup-secondary {
  background: #888;
  color: #fff;
}

.popup-secondary:hover:not(:disabled) {
  background: #555;
}

.popup-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mobile responsiveness */
@media (max-width: 480px) {
  .popup-dialog {
    margin: 20px;
    max-width: none;
    padding: 28px 24px 20px 24px;
  }
  
  .popup-title {
    font-size: 1.3rem;
  }
  
  .popup-content p {
    font-size: 0.95rem;
  }
  
  .popup-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  
  .popup-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
